// Player roster configuration — club players that represent the club
// in tournaments and leagues.
//
// Fase 1: players carry a 6-stat padel breakdown. `level` is the rounded
// mean of the stats (computeLevel) and stays persisted, so everything
// downstream (effective rating, salaries, market ranges, MatchSimulator)
// is untouched — the stats are additive detail, not a rating rewrite.

import { Rng, defaultRng, rollInt } from '@/game/utils/rng'

export const STAT_KEYS = ['drive', 'volea', 'remate', 'defensa', 'fisico', 'mental'] as const
export type StatKey = typeof STAT_KEYS[number]
export type PlayerStats = Record<StatKey, number> // each 1-99

export const STAT_LABELS: Record<StatKey, string> = {
  drive: 'Drive',
  volea: 'Volea',
  remate: 'Bandeja/Remate',
  defensa: 'Defensa',
  fisico: 'Físico',
  mental: 'Mental',
}

export type InjuryType = 'muscular' | 'articular' | 'sobrecarga' | 'mental'
export type InjurySeverity = 'leve' | 'moderada' | 'grave'

export interface Injury {
  type: InjuryType
  name: string // Spanish display, e.g. 'Rotura fibrilar'
  severity: InjurySeverity
  startDay: number
  totalDays: number
  daysRemaining: number
}

/** Spanish injury names per type × severity */
export const INJURY_TABLE: Record<InjuryType, Record<InjurySeverity, string>> = {
  muscular: {
    leve: 'Sobrecarga muscular',
    moderada: 'Rotura fibrilar',
    grave: 'Rotura muscular grave',
  },
  articular: {
    leve: 'Esguince de tobillo leve',
    moderada: 'Lesión de rodilla',
    grave: 'Rotura de ligamentos',
  },
  sobrecarga: {
    leve: 'Molestias en el codo',
    moderada: 'Epicondilitis (codo de pádel)',
    grave: 'Lesión crónica de muñeca',
  },
  mental: {
    leve: 'Falta de confianza',
    moderada: 'Ansiedad competitiva',
    grave: 'Burnout deportivo',
  },
}

/** Recovery days [min, max] per severity */
export const INJURY_DURATION: Record<InjurySeverity, [number, number]> = {
  leve: [5, 12],
  moderada: [13, 27],
  grave: [28, 56],
}

const INJURY_TYPES: InjuryType[] = ['muscular', 'articular', 'sobrecarga', 'mental']
const INJURY_TYPE_WEIGHTS = [4, 3, 2, 1] // muscular most common, mental rare
const SEVERITY_ROLL: Array<[InjurySeverity, number]> = [
  ['leve', 0.6],
  ['moderada', 0.3],
  ['grave', 0.1],
]

/** Roll a fresh injury (type, severity, duration) for the given day */
export function rollInjury(day: number, rng: Rng = defaultRng): Injury {
  const totalWeight = INJURY_TYPE_WEIGHTS.reduce((a, b) => a + b, 0)
  let typeRoll = rng() * totalWeight
  let type: InjuryType = 'muscular'
  for (let i = 0; i < INJURY_TYPES.length; i++) {
    typeRoll -= INJURY_TYPE_WEIGHTS[i]
    if (typeRoll <= 0) { type = INJURY_TYPES[i]; break }
  }

  let severity: InjurySeverity = 'grave'
  let severityRoll = rng()
  for (const [level, chance] of SEVERITY_ROLL) {
    if (severityRoll < chance) { severity = level; break }
    severityRoll -= chance
  }

  const [min, max] = INJURY_DURATION[severity]
  const totalDays = rollInt(min, max, rng)

  return {
    type,
    name: INJURY_TABLE[type][severity],
    severity,
    startDay: day,
    totalDays,
    daysRemaining: totalDays,
  }
}

/** Healthy = available for lineups (leagues, tournaments, circuit) */
export function isAvailable(player: Player): boolean {
  return player.injury === null
}

export interface Player {
  id: string
  name: string
  age: number // 17-38
  level: number // 1-100 base rating — the rounded mean of `stats`
  potential: number // ceiling for level growth
  form: number // 0-100, fluctuates weekly, regresses to 50
  salary: number // EUR per week
  signingFee: number // one-off cost to sign
  joinedDay: number
  matchesPlayed: number
  matchesWon: number
  stats: PlayerStats
  injury: Injury | null
  fatigue: number // 0-100; matches add, weeks recover
  /** Market candidates: the signing fee was already haggled (once per player) */
  negotiated?: boolean
}

/** One weekly datapoint of a player's trajectory (ficha sparkline/report) */
export interface PlayerSnapshot {
  day: number
  level: number
  form: number
}

/** How many weekly snapshots to keep per player (~6 game months) */
export const PLAYER_HISTORY_LIMIT = 24

export const ROSTER_RULES = {
  leagueMinPlayers: 4, // two pairs
  marketSize: 4,
  severanceWeeks: 4, // weeks of salary paid when releasing a player
} as const

/** Roster cap grows with the club: 6 slots when the roster unlocks (club
 *  level 3), +1 per level, capped at 10. */
export function getRosterCap(clubLevel: number): number {
  return Math.min(10, 6 + Math.max(0, clubLevel - 3))
}

// Split by gender so the portrait pool can infer which set to draw from
export const FIRST_NAMES_MALE = [
  'Alejandro', 'Carlos', 'David', 'Javier', 'Pablo', 'Sergio', 'Adrian',
  'Miguel', 'Raul', 'Ivan', 'Hugo', 'Marcos', 'Jorge', 'Alberto', 'Dani',
]

export const FIRST_NAMES_FEMALE = [
  'Lucia', 'Maria', 'Paula', 'Carla', 'Sara', 'Elena', 'Claudia', 'Marta',
  'Laura', 'Andrea', 'Nerea', 'Alba', 'Irene',
]

const FIRST_NAMES = [...FIRST_NAMES_MALE, ...FIRST_NAMES_FEMALE]

const LAST_NAMES = [
  'Garcia', 'Martinez', 'Lopez', 'Sanchez', 'Gonzalez', 'Fernandez',
  'Rodriguez', 'Perez', 'Gomez', 'Ruiz', 'Diaz', 'Moreno', 'Alvarez',
  'Romero', 'Navarro', 'Torres', 'Ramirez', 'Gil', 'Serrano', 'Molina',
  'Castro', 'Ortega', 'Delgado', 'Vargas', 'Iglesias', 'Leon', 'Vega',
]

/** Player quality range available on the market for a given club level */
export function getMarketLevelRange(clubLevel: number): [number, number] {
  if (clubLevel >= 7) return [65, 90]
  if (clubLevel >= 5) return [50, 75]
  if (clubLevel >= 3) return [30, 55]
  return [20, 40]
}

export function calculateSalary(level: number): number {
  return Math.round(level * 6)
}

export function calculateSigningFee(level: number, age: number): number {
  // Younger players cost more (more years and headroom ahead)
  const youthFactor = age <= 22 ? 1.5 : age <= 28 ? 1.2 : age <= 32 ? 1.0 : 0.7
  return Math.round(level * 40 * youthFactor)
}

export function generatePlayerName(rng: Rng = defaultRng): string {
  const first = FIRST_NAMES[Math.floor(rng() * FIRST_NAMES.length)]
  const last = LAST_NAMES[Math.floor(rng() * LAST_NAMES.length)]
  return `${first} ${last}`
}

/** Pair name in classic padel draw style: "Garcia / Lopez" */
export function generatePairName(rng: Rng = defaultRng): string {
  const a = LAST_NAMES[Math.floor(rng() * LAST_NAMES.length)]
  let b = LAST_NAMES[Math.floor(rng() * LAST_NAMES.length)]
  if (b === a) b = LAST_NAMES[(LAST_NAMES.indexOf(a) + 1) % LAST_NAMES.length]
  return `${a} / ${b}`
}

/** Round mean of the six stats — the single source of `level`. */
export function computeLevel(stats: PlayerStats): number {
  return Math.round(STAT_KEYS.reduce((sum, k) => sum + stats[k], 0) / STAT_KEYS.length)
}

/**
 * Distribute a target level into six stats with zero-sum jitter, so that
 * computeLevel(stats) === level EXACTLY: three (+d, −d) pairs are dealt to
 * shuffled stat slots, with d bounded to stay inside [1, 99].
 */
export function generateStats(level: number, rng: Rng = defaultRng): PlayerStats {
  const clamped = Math.max(1, Math.min(99, Math.round(level)))
  const jitterMax = Math.min(10, clamped - 1, 99 - clamped)

  const deltas: number[] = []
  for (let i = 0; i < STAT_KEYS.length / 2; i++) {
    const d = rollInt(0, jitterMax, rng)
    deltas.push(d, -d)
  }
  // Fisher-Yates with the provided rng (deterministic per seed)
  for (let i = deltas.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[deltas[i], deltas[j]] = [deltas[j], deltas[i]]
  }

  const stats = {} as PlayerStats
  STAT_KEYS.forEach((key, i) => {
    stats[key] = clamped + deltas[i]
  })
  return stats
}

/**
 * Nudge stats until their mean matches a new level (monthly progression /
 * decline). Keeps the stats ↔ level invariant without changing how level
 * evolution itself is rolled.
 */
export function applyLevelToStats(stats: PlayerStats, targetLevel: number, rng: Rng = defaultRng): PlayerStats {
  const next = { ...stats }
  let guard = 0
  while (computeLevel(next) !== targetLevel && guard < 200) {
    guard++
    const direction = computeLevel(next) < targetLevel ? 1 : -1
    // Pick a random stat with headroom in the needed direction
    const candidates = STAT_KEYS.filter(k =>
      direction > 0 ? next[k] < 99 : next[k] > 1,
    )
    if (candidates.length === 0) break
    const key = candidates[Math.floor(rng() * candidates.length)]
    next[key] += direction
  }
  return next
}

/** Generate a market candidate appropriate for the club's level */
export function generatePlayer(clubLevel: number, currentDay: number, rng: Rng = defaultRng): Player {
  const [minLevel, maxLevel] = getMarketLevelRange(clubLevel)
  const level = rollInt(minLevel, maxLevel, rng)
  const age = rollInt(17, 38, rng)
  // Younger players have more headroom above their current level
  const headroom = Math.max(0, 38 - age)
  const potential = Math.min(99, level + rollInt(0, headroom, rng))

  return {
    id: `player-${currentDay}-${Math.floor(rng() * 1e9).toString(36)}`,
    name: generatePlayerName(rng),
    age,
    level,
    potential,
    form: rollInt(40, 60, rng),
    salary: calculateSalary(level),
    signingFee: calculateSigningFee(level, age),
    joinedDay: currentDay,
    matchesPlayed: 0,
    matchesWon: 0,
    stats: generateStats(level, rng),
    injury: null,
    fatigue: 0,
  }
}

/** Effective playing strength: rating blended with current form */
export function getEffectiveRating(player: Player): number {
  return player.level * 0.7 + player.form * 0.3
}

/**
 * Scouting: the exact potential is hidden unless the club has a tactically
 * strong coach. Without one, show a deterministic fuzzy bucket around it
 * (same player always shows the same range — no re-roll snooping).
 */
export function formatPotential(player: Player, scouted: boolean): string {
  if (scouted) return `${player.potential}`
  const lo = Math.max(player.level, Math.floor((player.potential - 5) / 5) * 5)
  const hi = Math.min(99, Math.ceil((player.potential + 5) / 5) * 5)
  return `${lo}-${hi}`
}
